import { ANALYTICS_ENABLED, GA_MEASUREMENT_ID, isUntrackedPath } from "./config";
import { getConsent, onConsentChange } from "./consent";
import { getPageLanguage, getPlatform, getVisit, withStoreAttribution } from "./attribution";

/* ============================================================================
   Google Analytics 4 for the public website.

   How it works:
   1. startAnalytics() runs once when the page loads (src/index.js).
   2. GA's script is only downloaded after the visitor clicks "Accept" in the
      cookie banner. Before that — or after "Decline" — nothing is sent.
   3. track("event_name", { …params }) sends an event. Every event automatically
      gets page_language, landing_page, traffic_source, campaign and platform.
   4. App Store / Google Play clicks and language changes are picked up
      automatically from link clicks (see handleClick), so components don't
      need to call anything for those.

   Events (also listed in docs/ANALYTICS.md):
     page_view          every public page
     select_language    a link to another language was clicked   (from_language, to_language, placement)
     quiz_start         "Start" pressed on the free practice test (question_count)
     quiz_complete      last question answered                    (score, question_count, passed)
     app_store_click    App Store badge clicked                   (placement)
     google_play_click  Google Play badge clicked                 (placement)

   No personal data: only the parameter names in ALLOWED_PARAMS are ever sent,
   text values are shortened, and URLs are sent as paths without query strings.
   ========================================================================== */

const ALLOWED_PARAMS = new Set([
  "page_language", "landing_page", "traffic_source", "campaign", "platform",
  "placement", "from_language", "to_language", "score", "question_count", "passed",
]);

let started = false;
let gaLoaded = false;
let pageViewSent = false;
const queue = []; // events tracked before consent was given on this page

function consentGranted() {
  return ANALYTICS_ENABLED && getConsent() === "granted" && !isUntrackedPath(window.location.pathname);
}

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments); // gtag expects the `arguments` object, not an array
}

function loadGoogleAnalytics() {
  if (gaLoaded) return;
  gaLoaded = true;

  gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",          // no advertising use
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,              // we send page_view ourselves, with our parameters
    allow_google_signals: false,        // no cross-device / demographics
    allow_ad_personalization_signals: false,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/* Parameters every event carries. */
function commonParams() {
  const visit = getVisit();
  return {
    page_language: getPageLanguage(),
    landing_page: visit.landing_page,
    traffic_source: visit.traffic_source,
    campaign: visit.campaign,
    platform: getPlatform(),
  };
}

function sanitize(params) {
  const clean = {};
  Object.entries(params).forEach(([key, value]) => {
    if (!ALLOWED_PARAMS.has(key) || value === undefined || value === null) return;
    clean[key] = typeof value === "string" ? value.slice(0, 100) : value;
  });
  return clean;
}

function send(name, params) {
  gtag("event", name, sanitize({ ...commonParams(), ...params }));
}

/* Send an analytics event (ignored until the visitor accepts cookies). */
export function track(name, params = {}) {
  if (!consentGranted()) {
    // Not decided yet: hold a few events in case the visitor accepts on this page.
    // Declined: drop it — events from a period without consent are never sent.
    if (ANALYTICS_ENABLED && getConsent() === null && queue.length < 10) queue.push([name, params]);
    return;
  }
  send(name, params);
}

/* ---- duplicate protection -------------------------------------------------
   Remembers keys for this page load. trackOnce("quiz_complete:3", …) sends the
   event the first time only, so double clicks or re-renders can't double-count. */
const sentKeys = new Map();
export function trackOnce(key, name, params, { withinMs = Infinity } = {}) {
  const now = Date.now();
  if (sentKeys.has(key) && now - sentKeys.get(key) < withinMs) return;
  sentKeys.set(key, now);
  track(name, params);
}

/* ---- automatic click tracking --------------------------------------------- */

/* Where on the page a link is: nearest data-track-placement="…" (e.g. "hero"). */
function placementOf(element) {
  return element.closest("[data-track-placement]")?.getAttribute("data-track-placement") || "other";
}

function handleClick(event) {
  const link = event.target.closest && event.target.closest("a[href]");
  if (!link) return;
  const href = link.href;
  const placement = placementOf(link);

  if (href.startsWith("https://apps.apple.com/")) {
    link.href = withStoreAttribution(href, "app_store", placement);
    trackOnce(`app_store:${placement}`, "app_store_click", { placement }, { withinMs: 2000 });
  } else if (href.startsWith("https://play.google.com/")) {
    link.href = withStoreAttribution(href, "google_play", placement);
    trackOnce(`google_play:${placement}`, "google_play_click", { placement }, { withinMs: 2000 });
  } else if (link.hasAttribute("hreflang")) {
    const to = link.getAttribute("hreflang");
    const from = getPageLanguage();
    if (to !== from || window.location.pathname === "/") {
      track("select_language", { from_language: window.location.pathname === "/" ? "(chooser)" : from, to_language: to, placement });
    }
  }
}

/* ---- start --------------------------------------------------------------- */

function sendPageView() {
  if (pageViewSent) return;
  pageViewSent = true;
  send("page_view", {});
}

function onConsentGranted() {
  loadGoogleAnalytics();
  sendPageView();
  while (queue.length) send(...queue.shift());
}

function deleteAnalyticsCookies() {
  const domain = window.location.hostname.replace(/^www\./, "");
  document.cookie.split(";").map((c) => c.trim().split("=")[0])
    .filter((name) => name === "_ga" || name.startsWith("_ga_"))
    .forEach((name) => {
      [`domain=.${domain}`, `domain=${domain}`, ""].forEach((d) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; ${d}`;
      });
    });
}

export function startAnalytics() {
  if (started || !ANALYTICS_ENABLED || isUntrackedPath(window.location.pathname)) return;
  started = true;

  // Store links get campaign info for every visitor; events only with consent.
  document.addEventListener("click", handleClick, true);

  if (consentGranted()) onConsentGranted();

  onConsentChange((choice) => {
    if (choice === "granted") {
      onConsentGranted();
    } else {
      queue.length = 0;
      if (gaLoaded) gtag("consent", "update", { analytics_storage: "denied" });
      deleteAnalyticsCookies();
    }
  });
}
